// Auto-generated by scripts/split_calculators.py — keeps deterministic oilfield formulas in per-category modules.
import { round, num, positive, toRad } from "./_common";
// eslint-disable-next-line no-unused-vars
const _imports_ok = { round, num, positive, toRad };

// ===== Category: hydraulics =====
// --- Hydraulics (simple) ---
// =========================================================================
// HYDRAULICS (simple)
// =========================================================================

export function annularVelocity({ flowRate, dh, dp }) {
  const Q = positive(num(flowRate, "Flow Rate"), "Flow Rate");
  const Dh = positive(num(dh, "Hole/Csg ID"), "Hole/Csg ID");
  const Dp = positive(num(dp, "Pipe OD"), "Pipe OD");
  if (Dp >= Dh) throw new Error("Pipe OD must be less than hole ID");
  // AV (ft/min) = (Q gpm × 24.5) / (Dh² − Dp²)
  const av = (Q * 24.5) / (Dh * Dh - Dp * Dp);
  return {
    result: round(av, 1),
    unit: "ft/min",
    formula: "AV = (24.5 × Q) / (Dh² − Dp²)",
    steps: [
      `Q=${Q} gpm, Dh=${Dh} in, Dp=${Dp} in`,
      `AV = (24.5 × ${Q}) / (${Dh}² − ${Dp}²) = ${round(av, 1)} ft/min`,
    ],
    inputs: { "Flow Rate (gpm)": Q, "Hole ID (in)": Dh, "Pipe OD (in)": Dp },
  };
}

// --- Hydraulics — expanded ---
// =========================================================================
// HYDRAULICS — expanded
// =========================================================================

export function ecd({ mw, annPressureLoss, tvd }) {
  const M = positive(num(mw, "Mud Weight"), "Mud Weight");
  const dP = num(annPressureLoss, "Annular Pressure Loss");
  if (dP < 0) throw new Error("Annular pressure loss must be ≥ 0");
  const D = positive(num(tvd, "TVD"), "TVD");
  const ecdVal = M + dP / (0.052 * D);
  return {
    result: round(ecdVal, 3),
    unit: "ppg",
    formula: "ECD = MW + ΔP_ann / (0.052 × TVD)",
    steps: [
      `MW=${M} ppg, ΔP_ann=${dP} psi, TVD=${D} ft`,
      `ECD = ${M} + ${dP} / (0.052 × ${D})`,
      `ECD = ${M} + ${round(dP / (0.052 * D), 3)} = ${round(ecdVal, 3)} ppg`,
    ],
    inputs: { "MW (ppg)": M, "ΔP_ann (psi)": dP, "TVD (ft)": D },
  };
}

export function annularPressureLoss({ mw, pv, yp, dh, dp, length, flowRate }) {
  // Bingham plastic annular pressure drop, field units
  const M = positive(num(mw, "Mud Weight"), "Mud Weight");
  const PV = positive(num(pv, "Plastic Viscosity"), "Plastic Viscosity");
  const YP = positive(num(yp, "Yield Point"), "Yield Point");
  const Dh = positive(num(dh, "Hole/Csg ID"), "Hole/Csg ID");
  const Dp = positive(num(dp, "Pipe OD"), "Pipe OD");
  if (Dp >= Dh) throw new Error("Pipe OD must be less than hole ID");
  const L = positive(num(length, "Length"), "Length");
  const Q = positive(num(flowRate, "Flow Rate"), "Flow Rate");
  const gap = Dh - Dp;
  const AV = (Q * 24.5) / (Dh * Dh - Dp * Dp); // ft/min
  // ΔP = (PV × L × AV) / (60000 × gap²)  +  (YP × L) / (200 × gap)
  const dPvisc = (PV * L * AV) / (60000 * gap * gap);
  const dPyp = (YP * L) / (200 * gap);
  const dP = dPvisc + dPyp;
  return {
    result: round(dP, 1),
    unit: "psi",
    formula: "ΔP = (PV·L·AV)/(60000·(Dh−Dp)²) + (YP·L)/(200·(Dh−Dp))",
    steps: [
      `AV = (24.5 × ${Q}) / (${Dh}² − ${Dp}²) = ${round(AV, 1)} ft/min`,
      `Viscous term = (${PV} × ${L} × ${round(AV, 1)}) / (60000 × ${gap}²) = ${round(dPvisc, 2)} psi`,
      `Yield term = (${YP} × ${L}) / (200 × ${gap}) = ${round(dPyp, 2)} psi`,
      `ΔP = ${round(dPvisc, 2)} + ${round(dPyp, 2)} = ${round(dP, 1)} psi`,
    ],
    inputs: { "MW": M, "PV (cP)": PV, "YP (lb/100ft²)": YP, "Dh (in)": Dh, "Dp (in)": Dp, "Length (ft)": L, "Q (gpm)": Q },
    extras: { "Annular Velocity (ft/min)": round(AV, 1) },
  };
}

export function surgeSwabPressure({ mw, pipeVelocity, dh, dp, length, pv, yp, mode }) {
  // Simplified Burkhardt closed-pipe / open-pipe with Bingham approximation
  const M = positive(num(mw, "Mud Weight"), "Mud Weight");
  const V = positive(num(pipeVelocity, "Pipe Velocity"), "Pipe Velocity"); // ft/min
  const Dh = positive(num(dh, "Hole ID"), "Hole ID");
  const Dp = positive(num(dp, "Pipe OD"), "Pipe OD");
  if (Dp >= Dh) throw new Error("Pipe OD must be less than hole ID");
  const L = positive(num(length, "Length"), "Length");
  const PV = positive(num(pv, "Plastic Viscosity"), "Plastic Viscosity");
  const YP = positive(num(yp, "Yield Point"), "Yield Point");
  const m = (mode || "surge").toLowerCase();
  // Effective annular velocity from running closed pipe (Burkhardt clinging K ≈ 0.45)
  const K = 0.45;
  const Veff = V * (Dp * Dp / (Dh * Dh - Dp * Dp) + K);
  const gap = Dh - Dp;
  const dPvisc = (PV * L * Veff) / (60000 * gap * gap);
  const dPyp = (YP * L) / (200 * gap);
  const dP = dPvisc + dPyp;
  const signed = m === "swab" ? -dP : dP;
  return {
    result: round(signed, 1),
    unit: m === "swab" ? "psi (reduction)" : "psi (increase)",
    formula: "Veff = V·(Dp²/(Dh²−Dp²) + K);  ΔP = (PV·L·Veff)/(60000·gap²) + (YP·L)/(200·gap)",
    steps: [
      `Mode = ${m.toUpperCase()}  (K = 0.45 clinging constant, closed pipe)`,
      `Veff = ${V} × (${Dp}²/(${Dh}²−${Dp}²) + 0.45) = ${round(Veff, 1)} ft/min`,
      `Viscous = (${PV} × ${L} × ${round(Veff, 1)}) / (60000 × ${gap}²) = ${round(dPvisc, 2)} psi`,
      `Yield   = (${YP} × ${L}) / (200 × ${gap}) = ${round(dPyp, 2)} psi`,
      `ΔP = ${round(dP, 1)} psi → ${m === "swab" ? "subtract from" : "add to"} hydrostatic`,
    ],
    inputs: { Mode: m, "MW (ppg)": M, "V_pipe (ft/min)": V, "Dh (in)": Dh, "Dp (in)": Dp, "Length (ft)": L, "PV (cP)": PV, "YP (lb/100ft²)": YP },
    extras: { "Effective Annular Velocity (ft/min)": round(Veff, 1) },
  };
}

export function bitHHP({ flowRate, mw, nozzles, bitDiameter }) {
  const Q = positive(num(flowRate, "Flow Rate"), "Flow Rate");
  const M = positive(num(mw, "Mud Weight"), "Mud Weight");
  // nozzles: string like "12,12,12" in 32nds of an inch
  const arr = String(nozzles || "").split(",").map((s) => Number(s.trim())).filter((n) => !Number.isNaN(n) && n > 0);
  if (arr.length === 0) throw new Error("Provide at least one nozzle in 32nds (e.g. 12,12,12)");
  // Area in in² = Σ π/4 × (n/32)²
  let An = 0;
  arr.forEach((n) => { const d = n / 32; An += (Math.PI / 4) * d * d; });
  const dPbit = (M * Q * Q) / (12042 * An * An);
  const hhp = (Q * dPbit) / 1714;
  let hsi = null;
  if (bitDiameter) {
    const Db = num(bitDiameter, "Bit Diameter");
    if (Db > 0) {
      const Abit = (Math.PI / 4) * Db * Db;
      hsi = hhp / Abit;
    }
  }
  return {
    result: round(hhp, 1),
    unit: "HP",
    formula: "ΔP_bit = (MW·Q²)/(12042·An²);  HHP_bit = (Q·ΔP_bit)/1714",
    steps: [
      `Nozzles (32nds): ${arr.join(", ")}`,
      `An = Σ π/4 × (n/32)² = ${round(An, 4)} in²`,
      `ΔP_bit = (${M} × ${Q}²) / (12042 × ${round(An, 4)}²) = ${round(dPbit, 0)} psi`,
      `HHP_bit = (${Q} × ${round(dPbit, 0)}) / 1714 = ${round(hhp, 1)} HP`,
      hsi != null ? `HSI = HHP / A_bit = ${round(hsi, 2)} HP/in²` : "",
    ].filter(Boolean),
    inputs: { "Q (gpm)": Q, "MW (ppg)": M, "Nozzles (32nds)": arr.join(","), "Bit Dia (in)": bitDiameter || "—" },
    extras: {
      "Total Nozzle Area (in²)": round(An, 4),
      "ΔP_bit (psi)": round(dPbit, 0),
      ...(hsi != null ? { "HSI (HP/in²)": round(hsi, 2) } : {}),
    },
  };
}

